import { Component, Inject, OnInit, ViewChildren, QueryList } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent, Shared } from '@rubii/frontend-common';
import { interval, Subscription } from 'rxjs';
import { OtpStateService, OtpState } from '../otp-state.service';
import { Subscription as RxSub } from 'rxjs';
import { OtpInputComponent } from '../../../shared/otp-input/otp-input.component';

@Component({
  selector: 'app-otp-verification-dialog',
  templateUrl: './otp-verification-dialog.component.html',
  styleUrls: ['./otp-verification-dialog.component.scss']
})
export class OtpVerificationDialogComponent extends BaseComponent implements OnInit {
  @ViewChildren(OtpInputComponent) otpInputs: QueryList<OtpInputComponent>;

  otpValues: string[] = ['', '', '', '', '', ''];
  mobileNumber: string = '';
  maskedMobileNumber: string = '';

  currentStep = 1; // 1: OTP Sent 2: Verify OTP
  isVerifying = false;
  verifySuccess = false;
  shouldResendOtp = false;
  // pause state (timestamp in ms)
  pausedUntil = 0;
  isPaused = false;
  pauseRemainingSeconds = 0;
  showVerifyMsg = false;
  verifyMsg = '';
  
  private stateSub: RxSub | null = null;
  private pauseSub: Subscription | null = null;


  shared = Shared;

  constructor(
    public dialogRef: MatDialogRef<OtpVerificationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mobileNumber: string; onResend?: () => void; onSubmit?: (otp: string) => Promise<any> },
    private snack: MatSnackBar,
    private otpStateService: OtpStateService,
  ) {
    super();
    this.mobileNumber = data.mobileNumber;
  }

  ngOnInit(): void {
    // Mask the mobile number to show only last 4 digits
    if (this.mobileNumber) {
      const digits = this.mobileNumber.replace(/\D/g, '');
      const lastFour = digits.slice(-4);
      this.maskedMobileNumber = digits.length > 4 ? '*'.repeat(digits.length - 4) + lastFour : lastFour;
    }
    // initialize paused state from shared service (which reads cookie)
    this.stateSub = this.otpStateService.state$.subscribe((s: OtpState) => {
      this.pausedUntil = s.pausedUntil || 0;
      this.updatePauseFlags();
      this.startPauseTickerIfNeeded();
    });
  }

  ngOnDestroy(): void {
    if (this.pauseSub) {
      this.pauseSub.unsubscribe();
      this.pauseSub = null;
    }
    if (this.stateSub) {
      this.stateSub.unsubscribe();
      this.stateSub = null;
    }
  }

  private startPauseTickerIfNeeded() {
    const now = Date.now();
    if (!this.pausedUntil || this.pausedUntil <= now) {
      return;
    }
    if (this.pauseSub) {
      return;
    }
    this.pauseSub = interval(1000).subscribe(() => {
      this.updatePauseFlags();
      if (!this.isPaused) {
        this.stopPauseTicker();
      }
    });
  }

  private stopPauseTicker() {
    if (this.pauseSub) {
      this.pauseSub.unsubscribe();
      this.pauseSub = null;
    }
  }

  private updatePauseFlags() {
    const now = Date.now();
    if (this.pausedUntil && this.pausedUntil > now) {
      this.isPaused = true;
      this.pauseRemainingSeconds = Math.ceil((this.pausedUntil - now) / 1000);
      this.showVerifyMsg = true;
      this.verifySuccess = false;
      const minutes = Math.ceil(this.pauseRemainingSeconds / 60);
      if (minutes > 1) {
        this.verifyMsg = `3 Incorrect OTP entered. Please try again after ${Math.ceil(this.pauseRemainingSeconds / 60)} minutes.`;
      } else {
        this.verifyMsg = `3 Incorrect OTP entered. Please try again after ${this.pauseRemainingSeconds} seconds.`;
      }
      this.startPauseTickerIfNeeded();
    } else {
      this.isPaused = false;
      this.pauseRemainingSeconds = 0;
      this.verifyMsg = '';
      if (this.shouldResendOtp) {
        this.verifyMsg = 'OTP expired. Please request a new one.';
      }
      this.stopPauseTicker();
      const hadRealPause = !!(this.pausedUntil && this.pausedUntil > 0 && this.pausedUntil <= now);
      if (hadRealPause) {
        try {
          this.otpStateService.clearState();
        } catch (e) {
          // ignore
        }
      }
    }
  }

  onValueChange(data: { value: string, index: number }): void {
    this.otpValues[data.index] = data.value;
    // Trigger change detection
    this.otpValues = [...this.otpValues];
    // Check if all 6 digits are entered and auto-submit
    if (this.otpValues.every(val => val.length === 1)) {
      // Add a small delay to allow the UI to update
      setTimeout(() => {
        this.onSubmit();
      }, 100);
    }
  }

  onMoveNext(index: number): void {
    if (index < 5) {
      const nextInput = this.otpInputs.toArray()[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onMovePrevious(index: number): void {
    if (index > 0) {
      const prevInput = this.otpInputs.toArray()[index - 1];
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData && /^\d{6}$/.test(pastedData)) {
      for (let i = 0; i < 6; i++) {
        this.otpValues[i] = pastedData[i];
      }
      this.otpValues = [...this.otpValues];

      setTimeout(() => {
        this.onSubmit();
      }, 100);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onResendOtp(): void {
    try {
      if (this.data && typeof this.data.onResend === 'function') {
        this.data.onResend();
        this.openSnackBar(this.snack, 'OTP resent', 'close');
        this.shouldResendOtp = false;
        this.showVerifyMsg = false;
        this.verifyMsg = '';
      } else {
        this.openSnackBarWarning(this.snack, 'Unable to resend OTP', 'close');
      }
    } catch (e) {
      console.error('onResendOtp error', e);
      this.openSnackBarWarning(this.snack, 'Unable to resend OTP', 'close');
    }
  }

  onSubmit(): void {
    const otp = this.otpValues.join('');
    if (otp.length === 6) {
      this.currentStep = 2;
      this.isVerifying = true;
      this.showVerifyMsg = false;
      if (this.data && typeof this.data.onSubmit === 'function') {
        this.data.onSubmit(otp).then((result) => {
          // Update UI based on returned result
          this.isVerifying = false;
          this.showVerifyMsg = true;
          try {
            if (result && result.result) {
              this.verifySuccess = true;
              this.verifyMsg = 'Mobile number verification is successful';
            } else {
              this.verifySuccess = false;
              this.shouldResendOtp = result && result.message && result.message.toLowerCase().includes('expired');
              if (result && result.count === 0 && result.result === false) {
                let lastAttempt = result.lastAttempt || Date.now();
                const pasuedSeconds = result.pausedSeconds || 5 * 60; // default to 5 minutes if not provided
                const pausedUntil = this.otpStateService.setPauseFromLastAttempt(lastAttempt, pasuedSeconds * 1000);
                this.pausedUntil = pausedUntil || 0;
              }
              this.verifyMsg = result?.message || 'Invalid OTP';
            }
          } catch (e) {
            this.isVerifying = false;
            this.verifyMsg = 'Invalid OTP';
          }
        }).catch((e) => {
          this.isVerifying = false;
          this.verifyMsg = e?.message || 'Invalid OTP';
        });
      } else {
        this.isVerifying = false;
        this.verifyMsg = 'Verification handler not provided';
      }
    }
  }

  isSubmitDisabled(): boolean {
    return !this.otpValues.every(val => val.length === 1);
  }

  trackByIndex(index: number): number {
    return index;
  }
}